import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import categoryData from '../../data/categories.json';
import { useCart } from '../../context/CartContext';

export default function Header() {
    const location = useLocation();
    const { categories } = categoryData;
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const mobileMenuRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
            if (isMobileMenuOpen && mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
                setIsMobileMenuOpen(false);
                setIsMobileDropdownOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMobileMenuOpen]);

    const handleCategoryClick = (categoryId) => {
        setIsMobileMenuOpen(false);
        setIsMobileDropdownOpen(false);
        navigate(`/categories/${categoryId}`);
    };

    const { cart } = useCart();

    return (
        <section className="bg-coolGray-900 bs-section-dragged sticky top-0 z-50">
            <nav className="flex justify-between items-center p-4 sm:p-6 px-4 sm:px-12">
                {/* Left: Nav Items */}
                <div className="flex items-center w-full justify-between">
                    {/* Left: Logo (text only) */}
                    <div className="flex-shrink-0">
                        <div className="flex flex-col items-start">
                            <Link
                                to="/"
                                className="text-2xl font-extrabold tracking-tight text-white hover:text-green-300 transition-colors pb-0"
                            >
                                Bagga International
                                <span className="block h-1 w-full bg-green-500 rounded -mt-0.5"></span>
                                <span className="text-xs sm:text-sm font-medium text-coolGray-300 mt-0.5 tracking-wide" style={{ letterSpacing: '0.04em' }}>
                                    Tool Belts and More
                                </span>
                            </Link>
                        </div>
                    </div>
                    {/* Right: Nav Items */}
                    <div className="flex ml-auto items-center gap-6">
                        <ul className="hidden md:flex items-center gap-8">
    {/* Desktop Navigation */}
                            <li>
                                <Link to="/" className={`font-medium hover:text-coolGray-50 ${location.pathname === '/' ? 'text-white' : 'text-coolGray-400'}`}>Home</Link>
                            </li>
                            <li className="relative" ref={dropdownRef}>
                                <button
                                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                    className="text-coolGray-400 hover:text-coolGray-50 font-medium flex items-center focus:outline-none"
                                >
                                    Products
                                    <span className="ml-2">
                                        <svg
                                            className={`w-6 h-6 transform transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                                            fill="none"
                                            stroke="#9CA3AF"
                                            viewBox="0 0 24 24"
                                            xmlns="http://www.w3.org/2000/svg"
                                            style={{ strokeWidth: '3' }}
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M19 9l-7 7-7-7"
                                            />
                                        </svg>
                                    </span>
                                </button>
                                {isDropdownOpen && (
                                    <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-coolGray-800 ring-1 ring-black ring-opacity-5 z-50">
                                        <div className="py-1">
                                            {categories.map((category) => (
                                                <Link
                                                    key={category.id}
                                                    to={`/categories/${category.id}`}
                                                    className={`block px-4 py-2 text-sm text-coolGray-300 hover:bg-coolGray-700 hover:text-white ${location.pathname.startsWith(`/categories/${category.id}`) ? 'text-white bg-coolGray-700' : ''}`}
                                                    onClick={() => setIsDropdownOpen(false)}
                                                >
                                                    {category.name}
                                                </Link>
                                            ))}
                                            <Link
                                                to="/categories"
                                                className={`block px-4 py-2 text-sm text-coolGray-300 hover:bg-coolGray-700 hover:text-white ${location.pathname === '/categories' ? 'text-white bg-coolGray-700' : ''}`}
                                                onClick={() => setIsDropdownOpen(false)}
                                            >
                                                All Products
                                            </Link>
                                        </div>
                                    </div>
                                )}
                            </li>
                            <li>
                                <Link to="/about-us" className={`font-medium hover:text-coolGray-50 ${location.pathname === '/about-us' ? 'text-white' : 'text-coolGray-400'}`}>About Us</Link>
                            </li>
                            <li>
                                <Link to="/blog" className={`font-medium hover:text-coolGray-50 ${location.pathname.startsWith('/blog') ? 'text-white' : 'text-coolGray-400'}`}>Blog</Link>
                            </li>
                            <li>
                                <Link to="/contact" className={`font-medium hover:text-coolGray-50 ${location.pathname === '/contact' ? 'text-white' : 'text-coolGray-400'}`}>Contact</Link>
                            </li>
                            <li>
                                <Link to="/cart" aria-label="Cart" className={`relative flex items-center font-medium transition-colors group hover:text-green-400 ${location.pathname === '/cart' ? 'text-white' : 'text-coolGray-400'}`}>
                                    <svg width="22" height="22" fill="none" viewBox="0 0 24 24" className="text-green-400" style={{ marginRight: 4 }}>
                                        <path d="M6 6h15l-1.5 9h-13z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <circle cx="9" cy="21" r="1" fill="currentColor" />
                                        <circle cx="18" cy="21" r="1" fill="currentColor" />
                                    </svg>
                                    Cart
                                    <span className="absolute -top-2 -right-4 inline-flex items-center justify-center text-white text-xs font-bold rounded-full h-6 w-6 shadow-sm" 
                                        style={{ 
                                            backgroundColor: cart.length > 0 ? '#22c55e' : '#9ca3af',
                                            opacity: cart.length > 0 ? 1 : 0.7,
                                            transform: cart.length > 0 ? 'scale(1.05)' : 'scale(1)',
                                            transition: 'all 0.2s ease-in-out'
                                        }}>
                                        {cart.length}
                                    </span>
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>
                <button
                    className="block md:hidden self-center text-coolGray-400 hover:text-coolGray-50"
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                >
                    <svg width={42} height={42} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4 8H28M4 16H28M4 24H28" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
            </nav>
            {isMobileMenuOpen && (
                <div className="md:hidden fixed top-0 left-0 z-50 w-full h-full bg-coolGray-900 bg-opacity-50">
                    <div ref={mobileMenuRef} className="fixed top-0 left-0 bottom-0 w-4/6 max-w-xs bg-coolGray-900">
                        <nav className="relative p-6 h-full overflow-y-auto">
                            <div className="flex flex-col justify-between h-full">
                                <div>
                                    <div className="flex justify-end mb-4">
                                        <button
                                            className="text-coolGray-400 hover:text-coolGray-50 p-2"
                                            onClick={() => setIsMobileMenuOpen(false)}
                                        >
                                            <svg width={20} height={20} viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M1 1L11 11M1 11L11 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </button>
                                    </div>
                                    <ul className="py-6">
    {/* Mobile Navigation */}
                                        <li>
                                            <Link to="/" className={`block py-3 px-4 font-medium hover:text-white hover:bg-coolGray-800 rounded-md ${location.pathname === '/' ? 'text-white' : 'text-coolGray-400'}`} onClick={() => setIsMobileMenuOpen(false)}>Home</Link>
                                        </li>
                                        <li>
                                            <div className="py-3 px-4">
                                                <button
                                                    onClick={() => setIsMobileDropdownOpen(!isMobileDropdownOpen)}
                                                    className="text-coolGray-400 hover:text-white font-medium flex items-center focus:outline-none w-full justify-between"
                                                >
                                                    Products
                                                    <span className="ml-2">
                                                        <svg
                                                            className={`w-6 h-6 transform transition-transform duration-200 ${isMobileDropdownOpen ? 'rotate-180' : ''}`}
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                d="M19 9l-7 7-7-7"
                                                            />
                                                        </svg>
                                                    </span>
                                                </button>
                                                {isMobileDropdownOpen && (
                                                    <div className="mt-2 pl-4">
                                                        {categories.map((category) => (
                                                            <button
                                                                key={category.id}
                                                                onClick={() => handleCategoryClick(category.id)}
                                                                className="block w-full text-left py-2 text-sm text-coolGray-300 hover:text-white"
                                                            >
                                                                {category.name}
                                                            </button>
                                                        ))}
                                                        <Link
                                                            to="/categories"
                                                            className="block w-full text-left py-2 text-sm text-coolGray-300 hover:text-white"
                                                            onClick={() => {
                                                                setIsMobileMenuOpen(false);
                                                                setIsMobileDropdownOpen(false);
                                                            }}
                                                        >
                                                            All Products
                                                        </Link>
                                                    </div>
                                                )}
                                            </div>
                                        </li>
                                        <li>
                                            <Link to="/about-us" className={`block py-3 px-4 font-medium hover:text-white hover:bg-coolGray-800 rounded-md ${location.pathname === '/about-us' ? 'text-white' : 'text-coolGray-400'}`} onClick={() => setIsMobileMenuOpen(false)}>About Us</Link>
                                        </li>
                                        <li>
                                            <Link to="/blog" className={`block py-3 px-4 font-medium hover:text-white hover:bg-coolGray-800 rounded-md ${location.pathname.startsWith('/blog') ? 'text-white' : 'text-coolGray-400'}`} onClick={() => setIsMobileMenuOpen(false)}>Blog</Link>
                                        </li>
                                        <li>
                                            <Link to="/contact" className={`block py-3 px-4 font-medium hover:text-white hover:bg-coolGray-800 rounded-md ${location.pathname === '/contact' ? 'text-white' : 'text-coolGray-400'}`} onClick={() => setIsMobileMenuOpen(false)}>Contact</Link>
                                        </li>
                                        <li>
                                            <Link to="/cart" aria-label="Cart" className={`flex items-center py-3 px-4 font-medium hover:text-white hover:bg-coolGray-800 rounded-md transition-all duration-200 ease-in-out ${location.pathname === '/cart' ? 'text-white' : 'text-coolGray-400'}`} onClick={() => setIsMobileMenuOpen(false)}>
                                                <svg width="24" height="24" fill="none" viewBox="0 0 24 24" className="text-green-400" style={{ marginRight: 8 }}>
                                                    <path d="M6 6h15l-1.5 9h-13z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    <circle cx="9" cy="21" r="1" fill="currentColor" />
                                                    <circle cx="18" cy="21" r="1" fill="currentColor" />
                                                </svg>
                                                <span className="text-base">Cart</span>
                                                <span className="ml-2 inline-flex items-center justify-center text-white text-xs font-bold rounded-full h-6 w-6 shadow-sm" 
                                                    style={{ 
                                                        backgroundColor: cart.length > 0 ? '#22c55e' : '#9ca3af',
                                                        opacity: cart.length > 0 ? 1 : 0.7,
                                                        transform: cart.length > 0 ? 'scale(1.05)' : 'scale(1)',
                                                        transition: 'all 0.2s ease-in-out'
                                                    }}>
                                                    {cart.length}
                                                </span>
                                            </Link>
                                        </li>
                                        <li>

                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </nav>
                    </div>
                </div>
            )}

        </section>
    );
}